import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useEffect } from 'react';
import { useAuthStore } from '@stores';
import { AuthContextType, KeycloakProfile, KeycloakUser } from '../types';

export const useAuth = (): AuthContextType => {
  const auth = useOidcAuth();
  const { setUser, setLoading, setError, clearAuth } = useAuthStore();

  // Sync OIDC state with Zustand store
  useEffect(() => {
    setUser(auth.user as KeycloakUser | null);
  }, [auth.user, setUser]);

  useEffect(() => {
    setLoading(auth.isLoading);
  }, [auth.isLoading, setLoading]);

  useEffect(() => {
    setError(auth.error);
  }, [auth.error, setError]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      clearAuth();
    }
  }, [auth.isAuthenticated, clearAuth]);

  const user = auth.user as KeycloakUser | undefined;
  const profile = user?.profile as KeycloakProfile | undefined;

  return {
    isLoading: auth.isLoading,
    isAuthenticated: auth.isAuthenticated,
    user,
    profile,
    accessToken: user?.access_token,
    idToken: user?.id_token,
    error: auth.error,

    // Token utilities
    isTokenExpired: user?.expired || false,
    tokenExpiresAt: user?.expires_at,

    // Auth actions
    login: async (): Promise<void> => {
      await auth.signinRedirect();
    },
    logout: async (): Promise<void> => {
      await auth.signoutRedirect();
    },
    refreshToken: async (): Promise<void> => {
      await auth.signinSilent();
    },
  };
};
